import { getAllPageNames, getGauges } from '@config'
import { ROUTES } from '@constants/app_routes'
import type { GaugesVisibility } from '@containers/shipInfoPage/types'
import { getServerUrl } from '@hooks/useApi'
import Logger from '@logger'
import type { NullableString } from '@shared_types/global_types'
import type { WeatherWaveWatch } from '@shared_types/weather_forecast'
import { asArray, isNonEmptyArray } from '@shared_utils/array_utils'
import axios from 'axios'
import { isString, sample, snakeCase } from 'lodash'
import type { Map } from 'mapbox-gl'

import { toastError } from './toasts'

const logger = Logger.create('utils')

export const beaufortScaleInKn = {
  0: { min: 0, max: 1 },
  1: { min: 1, max: 4 },
  2: { min: 4, max: 7 },
  3: { min: 7, max: 11 },
  4: { min: 11, max: 17 },
  5: { min: 17, max: 22 },
  6: { min: 22, max: 28 },
  7: { min: 28, max: 34 },
  8: { min: 34, max: 41 },
  9: { min: 41, max: 48 },
  10: { min: 48, max: 56 },
  11: { min: 56, max: 64 },
  12: { min: 64, max: 72 }
}

interface FileReaderResult {
  file: File | null
  preview: ArrayBuffer | NullableString
}

export function readFileWithExtension (file: File, ext?: string[]) {
  return new Promise<FileReaderResult>((resolve, reject) => {
    const reader = new FileReader()

    const [type, extension] = file.type.split('/')

    let newExtension = extension

    if (ext?.length && !newExtension && !type && file.name.endsWith('.gpx')) {
      newExtension = 'gpx'
    }

    reader.onload = () => {
      resolve({ preview: reader.result, file })
    }

    reader.onerror = e => {
      reject(e)
    }

    if (ext && ext.includes(newExtension)) {
      if (type === 'image') {
        reader.readAsDataURL(file)
      } else {
        reader.readAsText(file)
      }
    } else if (ext) {
      const typesString = ext.map(ext => ext + ', ').join('')
      const errorMessage = `Only ${typesString} extensions are allowed`
      toastError(errorMessage)
    } else {
      reader.readAsText(file)
    }
  })
}

export const getWindyCurrentTsIndex = (data: WeatherWaveWatch, goal: number) => {
  const ts = asArray<number>(data.ts)
  const now = ts
    .filter(value => value !== null)
    .reduce((prev, curr) => (Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev))
  return ts.findIndex(item => now === item)
}

export const setYachtTransformation = (
  map: Map,
  heading: number,
  imageUrl?: string,
  showPulseAnimation?: boolean,
  yachtId?: string
) => {
  if (map) {
    logger.debug('setYachtTransformation', {
      heading,
      imageUrl,
      showPulseAnimation,
      yachtId
    })
  }
}

export enum PagesDescription {
  HOME = '<b>Home</b> page',
  DISTANCE = '<b>Distance</b> statistics page',
  SAT_ANTENNAS = 'Page with <b>Satellite Antennas</b> from mechanical components page',
  PROPELLERS = 'Page with <b>Propellers</b> from mechanical components page',
  RUDDER = 'Page with <b>Rudder</b> from mechanical components page',
  THRUSTER = 'Page with <b>Thruster</b> from mechanical components page',
  GEARBOX = 'Page with <b>Gearbox</b> from mechanical components page',
  CHILLER = 'Page with <b>Chiller</b> from mechanical components page',
  ENGINES = 'Page with <b>Engines</b> from mechanical components page',
  GENERATORS = 'Page with <b>Generators</b> from mechanical components page',
  TANKS = 'Page with <b>Tanks</b> from mechanical components page',
  STABILIZERS = 'Page with <b>Stabilizers</b> from mechanical components page',
  WATERMAKERS = 'Page with <b>Watermakers</b> from mechanical components page',
  TIMES = '<b>World clock</b> page',
  MEKKAH = '<b>Makkah</b> page',
  SPEED_DISTANCE = '<b>Speed</b> statistics page',
  DEPTH = '<b>Depth</b> statistics page',
  BAROMETRIC_PRESSURE = '<b>Barometric pressure</b> statistics page',
  WIND = '<b>Wind</b> statistics page',
  TEMPERATURES = '<b>Temperature</b> statistics page',
  THREE_D = '<b>3D</b> page',
  MAP = '<b>Map</b> page',
  DETAILS = '<b>Mechanical components</b> page with details',
  GALLERY = '<b>Gallery</b> page',
  VIDEO = 'Page with yacht <b>Video</b>',
  INFO = '<b>Dashboard</b> page',
  WEATHER = 'Page with <b>Weather</b>',
  INFO_CHANNEL = '<b>Info channel</b> page',
  YACHT_PAGE = 'Page with <b>Yacht information details</b>',
  CALENDAR = 'Page with <b>Calendar</b> and events upcoming',
  CREW = 'Page with <b>Crew</b> members',
  TRIP = 'Page with <b>Trip</b> and events upcoming',
  SAFETY_PLAN = 'Page with <b>Safety Plans</b>',
  PDF = 'Page with <b>PDF</b>',
  ECO_SCORE = '<b>Eco score</b> statistics page',
  BATTERY = 'Page with <b>Battery</b> from mechanical components page',
}

export const getPageTitle = (name: string) => {
  let label = PagesDescription[name.toUpperCase() as keyof typeof PagesDescription]
  if (!label) {
    label = `<b>${snakeCase(name).replace(/_/g, ' ')}</b>` as PagesDescription
  }

  return label
}

export enum TemperatureUnit {
  CELSIUS = '\xBAC',
  FAHRENHEIT = '\xBAF'
}

export const isAnyOfPagesEnabled = (pages?: ROUTES[]) => {
  if (pages) {
    let availablePages = getAllPageNames()
    availablePages = availablePages.map(page => ROUTES[page.toUpperCase() as keyof typeof ROUTES])

    return pages.some(page => availablePages.includes(page))
  }
  return true
}

const mapboxTokens: string[] = []

const apiKeyUrl = getServerUrl('/api/maps/api-keys')

const refreshMapboxApiKeys = () => {
  logger.verbose('refreshing Mapbox API keys list')

  axios
    .get(apiKeyUrl)
    .then(res => {
      if (isNonEmptyArray<string>(res.data)) {
        mapboxTokens.length = 0
        res.data.forEach(key => mapboxTokens.push(key))
        logger.verbose(`Mapbox tokens refreshed, got ${res.data.length} tokens`)
      }
    })
    .catch(err => logger.error(`could not refresh Mapbox tokens: ${err}`))
}

setTimeout(() => {
  setInterval(refreshMapboxApiKeys, 60 * 1000) // refresh tokens every minute
  refreshMapboxApiKeys()
}, 1)

export const getMapBoxApiKey = () => {
  const token = sample(mapboxTokens) ?? ''
  if (Math.random() > 1) {
    console.log('using Mapbox API key %s', token)
  }
  return token
}

export function getRouteToGetBackFromGraph () {
  return ROUTES.INFO
}

export function getDepthChartLimit (): number {
  const vw = Math.max(document.documentElement.clientWidth, window.innerWidth)
  const LeftDashboardWidth = 0.12 * vw
  const barSize = 16
  const rightOffset = 120

  return Math.floor((window.innerWidth - rightOffset - LeftDashboardWidth) / barSize)
}

export const getKeyByValue = <T>(object: T, value: string) => {
  return Object.keys(object as object).find(key => object[key as keyof T] === value)
}

export function smartRoundAndStringify (value: number, decimals = 2): string {
  const rounded = Math.round(value)
  if (Math.abs(rounded - value) < 0.1) {
    return `${rounded}`
  } else {
    return rounded.toFixed(decimals)
  }
}

export function getPropertyValue (value: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(value)
}

export function getRealVesselMaxSpeed (val: number | string): number {
  if (isString(val)) {
    val = parseFloat(val)
  }
  return !isNaN(val) && val > 0 ? val : 1
}

export const getAllTimezonesFromZone = (zoneName: string, timezones: string[]) => {
  return timezones.map(tz => ({
    timezone: tz,
    name: zoneName
  }))
}

export const getSideBarActiveGauges = () => {
  let order = 1
  const maxItemsOnLeft = 6
  const activeGauges: Partial<GaugesVisibility> = {}

  getGauges().forEach(gauge => {
    if (gauge.name !== 'Heading') { // this one always goes into center
      const key = gauge.name.toLowerCase() as keyof GaugesVisibility
      activeGauges[key] = { order, active: order <= maxItemsOnLeft }
      order++
    }
  })

  return activeGauges
}

export const calculateTextWidth = (text: string, fontWeight: string, fontSize: string, fontFamily: string) => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (context) {
    context.font = `${fontWeight} ${fontSize} ${fontFamily}`
    return context.measureText(text).width
  }
  return 0
}

export const breakTextIntoLines = (
  text: string,
  fontWeight: string,
  fontSize: string,
  fontFamily: string,
  maxWidth: number
): string[] => {
  const words = text.split(' ')
  const punctuation = [',', '.', '!', '?', ':', ';']
  let currentLine = ''
  const lines: string[] = []

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const width = calculateTextWidth(testLine, fontWeight, fontSize, fontFamily)

    if (width > maxWidth) {
      if (currentLine.length > 0 && punctuation.includes(currentLine.slice(-1))) {
        lines.push(currentLine.trim())
      } else {
        lines.push(currentLine.trim())
      }
      currentLine = word
    } else {
      currentLine = testLine
    }
  })

  if (currentLine) {
    lines.push(currentLine.trim())
  }

  return lines
}

export const remToPx = (rem: number) => {
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  )
  return isNaN(rootFontSize) ? 0 : rem * rootFontSize
}
